import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import CircledExclamationmarkIcon from 'icons/CircledExclamationmark.icon';
import { useTranslation } from 'react-i18next';
import {
  DownloadBannerContentProps,
  DownloadBannerStatus,
  DownloadBannerType,
  DOWNLOAD_BANNER_STATUS,
  DOWNLOAD_BANNER_TYPE,
} from 'models/Download';
import { Button, Loader } from 'ncoded-component-library';
import BannerWrapper from 'components/BannerWrapper';
import { COLOR_VARIANT } from 'models/Colors';
import toast, { Toast } from 'react-hot-toast';
import useToast from 'hooks/useToast';
import { FIVE_SECONDS } from 'data/global';
import { DOWNLOAD_BANNER_TITLE_COPY } from './data';

import './DownloadStatusBanner.styles.scss';

export type DownloadStatusBannerRef = {
  open: () => void;
  close: () => void;
};

type DownloadStatusBannerProps = {
  className?: string;
  variant: DownloadBannerStatus;
  type: DownloadBannerType;
  onClose: () => void;
} & DownloadBannerContentProps;

const DownloadStatusBanner: ForwardRefRenderFunction<
  DownloadStatusBannerRef,
  DownloadStatusBannerProps
> = (props, ref) => {
  const {
    className,
    variant,
    type = DOWNLOAD_BANNER_TYPE.FILE,
    message,
    customTitle,
    isClosable,
    onClose,
  } = props;

  const [isVisible, setIsVisible] = useState(false);

  const classes = classNames('download-status-banner', variant, className);

  const { t } = useTranslation();

  useImperativeHandle(
    ref,
    () => ({
      open: () => setIsVisible(true),
      close: () => setIsVisible(false),
    }),
    [],
  );

  const color = useMemo(() => {
    if (!isVisible) return null;

    if (variant === DOWNLOAD_BANNER_STATUS.PREPARING) {
      return COLOR_VARIANT.LIGHT_GREY;
    }

    if (variant === DOWNLOAD_BANNER_STATUS.SUCCESS) {
      return COLOR_VARIANT.LIGHT_GREEN;
    }
    return COLOR_VARIANT.RED;
  }, [variant, isVisible]);

  const icon = useMemo(() => {
    if (!isVisible) return null;

    if (variant === DOWNLOAD_BANNER_STATUS.PREPARING) {
      return <Loader overlay={false} size="small" />;
    }
    if (variant === DOWNLOAD_BANNER_STATUS.SUCCESS) {
      return <CheckcircleIcon />;
    }
    return <CircledExclamationmarkIcon />;
  }, [variant, isVisible]);

  const title = useMemo(() => {
    if (!isVisible) return null;

    return customTitle || t(DOWNLOAD_BANNER_TITLE_COPY[type][variant]);
  }, [customTitle, variant, isVisible, type, t]);

  useEffect(() => {
    if (variant !== DOWNLOAD_BANNER_STATUS.SUCCESS) return;

    const timeout = setTimeout(onClose, FIVE_SECONDS);

    return () => clearTimeout(timeout);
  }, [variant, onClose]);

  const render = useCallback(
    ({ id, visible }: Toast) => (
      <BannerWrapper
        className={classes}
        isVisible={visible}
        color={color}
        onClose={() => {
          toast.dismiss(id);
          setIsVisible(false);
          onClose();
        }}
      >
        <div className="download-status-banner__icon">{icon}</div>
        <div className="download-status-banner__content">
          <p className="download-status-banner__title">{title}</p>

          <div className="download-status-banner__message">
            {message && <p>{message}</p>}

            {isClosable && (
              <Button variant="link" onClick={onClose}>
                {t('understood')}
              </Button>
            )}
          </div>
        </div>
      </BannerWrapper>
    ),
    [classes, color, icon, isClosable, message, onClose, t, title],
  );

  useToast({
    visible: isVisible && !!variant,
    render,
  });

  return null;
};

export default forwardRef(DownloadStatusBanner);
